// src/pages/FeedbackPage.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { MessageSquarePlus } from 'lucide-react';
import feedbackService from '../services/feedbackService';
import { Btn, Card } from '../components/ui';

const TYPES = ['bug', 'suggestion', 'other'];

const FeedbackPage = ({ user, toast }) => {
  const { t } = useTranslation();
  const [type, setType] = useState('suggestion');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const isValid = type && description.trim().length >= 10;

  const handleSubmit = async () => {
    if (!isValid) return;
    setLoading(true);
    const r = await feedbackService.createFeedback({
      type,
      description: description.trim(),
      userName: user?.name || '',
      userEmail: user?.email || '',
    });
    setLoading(false);
    if (r.success) {
      toast(t('feedback.toast.success'), 'success');
      setType('suggestion');
      setDescription('');
    } else {
      toast(t('feedback.toast.error'), 'error');
    }
  };

  return (
    <div className="pt-24 pb-32 lg:pb-20 container mx-auto px-4 max-w-xl">
      <Helmet>
        <title>{t('feedback.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/feedback" />
      </Helmet>

      <Card className="p-6">
        <div className="text-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-fuchsia-600 rounded-full mx-auto flex items-center justify-center text-white mb-3">
            <MessageSquarePlus size={24} />
          </div>
          <h1 className="text-2xl font-bold dark:text-gray-100">{t('feedback.title')}</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">{t('feedback.subtitle')}</p>
        </div>

        {/* Type */}
        <div className="mb-5">
          <label className="block text-sm font-semibold mb-2 dark:text-gray-300">
            {t('feedback.typeLabel')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {TYPES.map((tp) => (
              <button
                key={tp}
                onClick={() => setType(tp)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition ${
                  type === tp
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
                data-testid={`feedback-type-${tp}`}
              >
                {t(`feedback.types.${tp}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Description */}
        <div className="mb-5">
          <label className="block text-sm font-semibold mb-2 dark:text-gray-300">
            {t('feedback.descLabel')}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('feedback.descPlaceholder')}
            maxLength={2000}
            rows={5}
            className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border-2 border-gray-100 dark:border-gray-700 focus:border-rose-300 dark:focus:border-rose-500 rounded-xl dark:text-gray-100 focus:outline-none transition resize-none"
            data-testid="feedback-description"
          />
          <div className="flex justify-between mt-1">
            {description.trim().length > 0 && description.trim().length < 10 && (
              <p className="text-red-500 text-xs">{t('feedback.descMin')}</p>
            )}
            <p className="text-gray-400 text-xs ml-auto">{description.length}/2000</p>
          </div>
        </div>

        {/* Submit */}
        <Btn
          onClick={handleSubmit}
          loading={loading}
          className="w-full"
          disabled={!isValid}
          data-testid="feedback-submit"
        >
          {t('feedback.submitBtn')}
        </Btn>
      </Card>
    </div>
  );
};

export default FeedbackPage;
